import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { AppDispatch, RootState } from "../../redux/store";
import {
  listenToIMessages,
  sendIMessage,
  addOptimisticIMessage,
  loadOlderIMessages,
  markIThreadAsRead,
  unsubscribeIMessage,
  clearThreadMessages,
  IMessage,
} from "../../redux/iMessageSlice";
import { ArrowLeft, Send, ShieldAlert, X, ChevronUp } from "lucide-react";

// ── Design tokens ─────────────────────────────────────────
const T = {
  gold:        "#C8860A",
  goldLight:   "#E8A020",
  goldPale:    "#FDF6E3",
  goldBorder:  "#EAD08A",
  black:       "#0A0A0A",
  offBlack:    "#1A1A1A",
  charcoal:    "#2C2C2C",
  white:       "#FFFFFF",
  surface:     "#F8F5F0",
  muted:       "#8A8070",
  mutedLight:  "#B8AFA0",
  border:      "#EDE8E0",
  bubbleMe:    "#0A0A0A",
  bubbleOther: "#F2EDE6",
};

// ── Helpers ───────────────────────────────────────────────
const toMs = (ts: any): number | null => {
  if (!ts) return null;
  if (ts?.toMillis) return ts.toMillis();
  if (ts?.seconds)  return ts.seconds * 1000;
  if (typeof ts === "number") return ts;
  return null;
};

const formatTime = (ts: any): string => {
  const ms = toMs(ts);
  if (!ms) return "";
  return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDateLabel = (ts: any): string => {
  const ms = toMs(ts);
  if (!ms) return "";
  const d         = new Date(ms);
  const today     = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString())     return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const violatesContactRules = (input: string): boolean => {
  if (!input) return false;
  if (/[0-9]/.test(input))                   return true;
  if (/[+@#%^*&()/\\\-_=]/.test(input))     return true;
  if (input.toLowerCase().includes(".com"))  return true;
  return false;
};

// ── Avatar ────────────────────────────────────────────────
const Avatar: React.FC<{ src?: string; name: string; size?: number }> = ({
  src, name, size = 36,
}) => {
  const initials = getInitials(name);
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: `linear-gradient(135deg, ${T.charcoal}, ${T.offBlack})`,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, overflow: "hidden",
      border: `1.5px solid ${T.border}`,
    }}>
      {src
        ? <img src={src} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        : <span style={{
            color: T.mutedLight,
            fontSize: Math.round(size * 0.34),
            fontWeight: 700,
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            letterSpacing: "0.04em",
          }}>
            {initials}
          </span>
      }
    </div>
  );
};

// ── Date pill ─────────────────────────────────────────────
const DatePill: React.FC<{ label: string }> = ({ label }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 10,
    margin: "18px 0 10px",
  }}>
    <div style={{ flex: 1, height: 1, background: T.border }} />
    <span style={{
      fontSize: 10, color: T.mutedLight, fontWeight: 500,
      letterSpacing: "0.1em", textTransform: "uppercase",
      fontFamily: "'DM Mono', monospace",
      whiteSpace: "nowrap",
    }}>
      {label}
    </span>
    <div style={{ flex: 1, height: 1, background: T.border }} />
  </div>
);

// ── Bubble ────────────────────────────────────────────────
const Bubble: React.FC<{
  msg: IMessage & { optimistic?: boolean; failed?: boolean };
  isMe: boolean;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  otherName: string;
  otherPhoto?: string;
}> = ({ msg, isMe, isFirstInGroup, isLastInGroup, otherName, otherPhoto }) => {
  const isFailed  = (msg as any).failed    === true;
  const isPending = (msg as any).optimistic === true;

  const meRadius =
    isFirstInGroup && isLastInGroup ? "20px 20px 4px 20px"
    : isFirstInGroup                ? "20px 20px 20px 4px"
    : isLastInGroup                 ? "4px 20px 4px 20px"
    :                                 "4px 20px 20px 4px";

  const otherRadius =
    isFirstInGroup && isLastInGroup ? "20px 20px 20px 4px"
    : isFirstInGroup                ? "4px 20px 20px 20px"
    : isLastInGroup                 ? "4px 20px 20px 4px"
    :                                 "4px 20px 20px 4px";

  return (
    <div style={{
      display: "flex",
      flexDirection: isMe ? "row-reverse" : "row",
      alignItems: "flex-end",
      gap: 8,
      marginBottom: isLastInGroup ? 8 : 2,
      marginTop: isFirstInGroup ? 6 : 0,
      paddingLeft: isMe ? 48 : 0,
      paddingRight: isMe ? 0 : 48,
    }}>
      {!isMe && (
        <div style={{ width: 28, flexShrink: 0 }}>
          {isLastInGroup && <Avatar src={otherPhoto} name={otherName} size={28} />}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", alignItems: isMe ? "flex-end" : "flex-start", gap: 2 }}>
        {!isMe && isFirstInGroup && (
          <span style={{
            fontSize: 11, color: T.muted, fontWeight: 500,
            paddingLeft: 4, letterSpacing: "0.02em",
          }}>
            {otherName}
          </span>
        )}

        <div style={{
          maxWidth: "100%",
          padding: "10px 14px",
          borderRadius: isMe ? meRadius : otherRadius,
          background: isFailed
            ? "#FFF1F2"
            : isMe
            ? `linear-gradient(135deg, ${T.offBlack}, ${T.charcoal})`
            : T.bubbleOther,
          border: isFailed ? "1px solid #FECDD3" : "none",
          boxShadow: isMe
            ? "0 2px 12px rgba(0,0,0,0.18)"
            : "0 1px 4px rgba(0,0,0,0.06)",
          opacity: isPending ? 0.6 : 1,
          transition: "opacity 0.2s",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}>
          {msg.isAIReply && !isMe && (
            <span style={{
              fontSize: 9, color: T.gold, fontWeight: 600,
              letterSpacing: "0.1em", textTransform: "uppercase",
              fontFamily: "'DM Mono', monospace",
            }}>
              ✦ auto reply
            </span>
          )}

          <span style={{
            fontSize: 14,
            lineHeight: 1.55,
            color: isFailed ? "#9F1239" : isMe ? T.white : T.charcoal,
            wordBreak: "break-word",
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 400,
          }}>
            {msg.text}
          </span>

          <span style={{
            fontSize: 10,
            alignSelf: "flex-end",
            color: isFailed
              ? "#F43F5E"
              : isMe
              ? "rgba(255,255,255,0.4)"
              : T.mutedLight,
            fontFamily: "'DM Mono', monospace",
          }}>
            {isFailed ? "Failed" : isPending ? "Sending…" : formatTime(msg.timestamp)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────
const IMessages: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();
  const { chatId } = useParams<{ chatId: string }>();

  const firebaseAuth   = getAuth();
  const firebaseUid    = firebaseAuth.currentUser?.uid ?? "";

  const { user, allUsers }                             = useSelector((s: RootState) => s.auth);
  const { threads, messages, hasMore, paginating }     = useSelector((s: RootState) => s.imessage);

  const currentUserId = firebaseUid || user?.userId || "";

  const [threadId,    setThreadId]    = useState<string | null>(null);
  const [text,        setText]        = useState("");
  const [showWarning, setShowWarning] = useState(false);
  const [isAtBottom,  setIsAtBottom]  = useState(true);

  const [resolvedOtherUserId, setResolvedOtherUserId] = useState<string>("");

  const chatRef  = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const activeThread = threads.find(t => t.threadId === chatId);
  const reduxOtherUserId = activeThread?.otherUserId ?? "";

  // FIX: robust derivation using indexOf instead of split("_").filter()
  // Firebase UIDs are 28 alphanumeric chars with no underscores, so the
  // threadId format is always "uidA_uidB" with exactly one underscore separator.
  const deriveOtherFromThreadId = useCallback(
    (tid: string, myUid: string): string => {
      if (!tid || !myUid) return "";
      const idx = tid.indexOf("_");
      if (idx === -1) return "";
      const a = tid.slice(0, idx);
      const b = tid.slice(idx + 1);
      if (a === myUid) return b;
      if (b === myUid) return a;
      // myUid not found in threadId — auth not settled yet, return empty
      return "";
    },
    []
  );

  // ── Resolve otherUserId with all three strategies ─────
  useEffect(() => {
    // Strategy 1: already have it from Redux inbox threads
    if (reduxOtherUserId) {
      setResolvedOtherUserId(reduxOtherUserId);
      return;
    }

    if (!chatId || !currentUserId) return;

    // Strategy 3: derive from threadId (instant, no network)
    const derived = deriveOtherFromThreadId(chatId, currentUserId);
    if (derived) {
      setResolvedOtherUserId(derived);
    }

    // Strategy 2: read thread doc from Firestore (fallback / confirmation)
    const db = getFirestore();
    getDoc(doc(db, "messages", chatId)).then(snap => {
      if (!snap.exists()) return;
      const data = snap.data();
      // Prefer the stored otherUserId field, fall back to participants
      const other =
        (typeof data.otherUserId === "string" && data.otherUserId !== ""
          ? data.otherUserId
          : null) ??
        (data.participants as string[] ?? []).find((p: string) => p !== currentUserId) ??
        "";
      if (other) setResolvedOtherUserId(other);
    }).catch(err => {
      console.warn("[IMessages] thread doc fetch failed:", err.message);
    });
  }, [reduxOtherUserId, chatId, currentUserId, deriveOtherFromThreadId]);

  const finalOtherUserId = reduxOtherUserId || resolvedOtherUserId;

  // ── Resolve display info ──────────────────────────────
  const otherUser  = allUsers?.find(u => u.userId === finalOtherUserId);
  const otherName  = otherUser
    ? `${otherUser.firstName ?? ""} ${otherUser.lastName ?? ""}`.trim() || "User"
    : "User";
  const otherPhoto = otherUser?.profilePicture;

  const threadMessages = threadId ? (messages[threadId] ?? []) : [];

  const canSend = !!text.trim() && !!threadId && !!currentUserId && !!finalOtherUserId;

  // ── Init thread ───────────────────────────────────────
  useEffect(() => {
    if (!chatId || !currentUserId) return;
    let mounted = true;

    setThreadId(chatId);

    const init = async () => {
      try {
        if (!mounted) return;
        dispatch(listenToIMessages({ threadId: chatId }));
        dispatch(markIThreadAsRead({ threadId: chatId }));
      } catch (err: any) {
        console.error("[IMessages] init failed:", err);
      }
    };

    init();

    return () => {
      mounted = false;
      unsubscribeIMessage(`messages_${chatId}`);
      dispatch(clearThreadMessages(chatId));
    };
  }, [dispatch, chatId, currentUserId]);

  // ── Mark as read when new messages arrive ────────────
  useEffect(() => {
    if (!threadId) return;
    dispatch(markIThreadAsRead({ threadId }));
  }, [threadMessages.length, threadId, dispatch]);

  // ── Auto scroll ───────────────────────────────────────
  const scrollToBottom = useCallback((smooth = false) => {
    requestAnimationFrame(() => {
      if (chatRef.current) {
        chatRef.current.scrollTo({
          top: chatRef.current.scrollHeight,
          behavior: smooth ? "smooth" : "auto",
        });
      }
    });
  }, []);

  useEffect(() => {
    if (isAtBottom) scrollToBottom();
  }, [threadMessages, isAtBottom, scrollToBottom]);

  // ── Scroll handler (+ pagination) ─────────────────────
  const handleScroll = useCallback(() => {
    const el = chatRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    setIsAtBottom(atBottom);

    if (el.scrollTop < 80 && threadId && hasMore[threadId] !== false && !paginating) {
      const prevHeight = el.scrollHeight;
      dispatch(loadOlderIMessages({ threadId })).then(() => {
        requestAnimationFrame(() => {
          if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight - prevHeight;
          }
        });
      });
    }
  }, [dispatch, threadId, hasMore, paginating]);

  // ── Auto-resize textarea ──────────────────────────────
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    if (violatesContactRules(v)) { setShowWarning(true); return; }
    setShowWarning(false);
    setText(v);
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + "px";
    }
  };

  // ── Send ──────────────────────────────────────────────
  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || !threadId || !currentUserId || !finalOtherUserId) return;
    if (violatesContactRules(trimmed)) { setShowWarning(true); return; }

    const optimisticId = `opt_${Date.now()}`;

    dispatch(addOptimisticIMessage({
      threadId,
      message: {
        id:         optimisticId,
        senderId:   currentUserId,
        text:       trimmed,
        timestamp:  Date.now(),
        read:       false,
        optimistic: true,
      } as any,
    }));

    setText("");
    setShowWarning(false);
    if (inputRef.current) inputRef.current.style.height = "auto";
    scrollToBottom(true);

    dispatch(sendIMessage({
      threadId,
      otherUserId: finalOtherUserId,
      text: trimmed,
      optimisticId,
    }))
      .unwrap()
      .catch(err => console.error("[IMessages] send failed:", err));

  }, [text, threadId, currentUserId, finalOtherUserId, dispatch, scrollToBottom]);

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // ── Group messages by date ────────────────────────────
  type GroupedMessages = {
    dateLabel: string;
    msgs: (IMessage & { optimistic?: boolean; failed?: boolean })[];
  }[];

  const grouped: GroupedMessages = [];
  threadMessages.forEach(msg => {
    const label = formatDateLabel(msg.timestamp);
    const last  = grouped[grouped.length - 1];
    if (!last || last.dateLabel !== label) {
      grouped.push({ dateLabel: label, msgs: [msg as any] });
    } else {
      last.msgs.push(msg as any);
    }
  });

  // ─────────────────────────────────────────────────────
  return (
    <div style={S.root}>
      <style>{CSS}</style>

      {/* ── Header ── */}
      <div style={S.header}>
        <button
          style={S.backBtn}
          onClick={() => navigate("/imessagepreview")}
          aria-label="Back"
        >
          <ArrowLeft size={18} color={T.charcoal} strokeWidth={2} />
        </button>

        <div style={S.headerCenter}>
          <Avatar src={otherPhoto} name={otherName} size={36} />
          <div style={S.headerInfo}>
            <span style={S.headerName}>{otherName}</span>
            <span style={S.headerSub}>Tap to view profile</span>
          </div>
        </div>

        <div style={{ width: 36 }} />
      </div>

      <div style={S.headerDivider} />

      {/* ── Chat area ── */}
      <div style={S.chatOuter}>
        {paginating && (
          <div style={S.loadMore}>
            <ChevronUp size={13} color={T.mutedLight} />
            <span style={S.loadMoreText}>Loading older messages</span>
          </div>
        )}

        <div ref={chatRef} style={S.messagesArea} onScroll={handleScroll}>
          {threadMessages.length === 0 && (
            <div style={S.emptyState} className="im-fade-in">
              <div style={S.emptyGlyph}>✦</div>
              <div style={S.emptyTitle}>Begin your conversation</div>
              <div style={S.emptySub}>
                Messages with {otherName.split(" ")[0]} are private and secure.
              </div>
            </div>
          )}

          {grouped.map(({ dateLabel, msgs }) => (
            <div key={dateLabel}>
              <DatePill label={dateLabel} />
              {msgs.map((msg, i) => {
                const isMe           = msg.senderId === currentUserId;
                const prevMsg        = msgs[i - 1];
                const nextMsg        = msgs[i + 1];
                const isFirstInGroup = !prevMsg || prevMsg.senderId !== msg.senderId;
                const isLastInGroup  = !nextMsg || nextMsg.senderId !== msg.senderId;

                return (
                  <div key={msg.id} className="im-msg-in">
                    <Bubble
                      msg={msg}
                      isMe={isMe}
                      isFirstInGroup={isFirstInGroup}
                      isLastInGroup={isLastInGroup}
                      otherName={otherName}
                      otherPhoto={otherPhoto}
                    />
                  </div>
                );
              })}
            </div>
          ))}

          <div style={{ height: 8 }} />
        </div>

        {!isAtBottom && (
          <button
            style={S.scrollFab}
            onClick={() => scrollToBottom(true)}
            className="im-fab"
            aria-label="Scroll to latest"
          >
            <ArrowLeft size={15} style={{ transform: "rotate(-90deg)" }} color={T.white} />
          </button>
        )}
      </div>

      {/* ── Contact info warning ── */}
      {showWarning && (
        <div style={S.warningBar} className="im-slide-up">
          <ShieldAlert size={14} color={T.gold} strokeWidth={2} />
          <span style={S.warningText}>
            Sharing contact info or external links isn't allowed.
          </span>
          <button
            style={S.warnClose}
            onClick={() => {
              setShowWarning(false);
              setText("");
              if (inputRef.current) inputRef.current.style.height = "auto";
            }}
          >
            <X size={13} color={T.muted} />
          </button>
        </div>
      )}

      {/* ── Input bar ── */}
      <div style={S.footer}>
        <div style={S.inputRow}>
          <textarea
            ref={inputRef}
            style={S.input}
            placeholder={`Message ${otherName.split(" ")[0] || "…"}…`}
            value={text}
            rows={1}
            onChange={handleTextChange}
            onKeyDown={handleKey}
            aria-label="Message input"
          />
          <button
            style={{
              ...S.sendBtn,
              background: canSend
                ? `linear-gradient(135deg, ${T.offBlack}, ${T.charcoal})`
                : T.surface,
              boxShadow: canSend ? "0 4px 16px rgba(0,0,0,0.25)" : "none",
              cursor:    canSend ? "pointer" : "default",
              transform: canSend ? "scale(1)" : "scale(0.92)",
            }}
            onClick={handleSend}
            disabled={!canSend}
            aria-label="Send"
          >
            <Send
              size={15}
              color={canSend ? T.gold : T.mutedLight}
              strokeWidth={2.5}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default IMessages;

// ── CSS ───────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@400;500&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  @keyframes imFadeIn  { from { opacity: 0; } to { opacity: 1; } }
  @keyframes imMsgIn   { from { opacity: 0; transform: translateY(8px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
  @keyframes imSlideUp { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes imFabIn   { from { opacity: 0; transform: scale(0.7); } to { opacity: 1; transform: scale(1); } }

  .im-fade-in  { animation: imFadeIn  0.4s ease both; }
  .im-msg-in   { animation: imMsgIn   0.22s ease both; }
  .im-slide-up { animation: imSlideUp 0.2s  ease both; }
  .im-fab      { animation: imFabIn   0.18s cubic-bezier(0.34,1.56,0.64,1) both; }

  textarea:focus { outline: none; }
  ::-webkit-scrollbar { width: 0; }
`;

// ── Styles ────────────────────────────────────────────────
const S: Record<string, React.CSSProperties> = {
  root: {
    display:         "flex",
    flexDirection:   "column",
    height:          "100dvh",
    maxWidth:        520,
    margin:          "0 auto",
    backgroundColor: "#FFFFFF",
    fontFamily:      "'DM Sans', sans-serif",
    borderLeft:      "1px solid #EDE8E0",
    borderRight:     "1px solid #EDE8E0",
    overflow:        "hidden",
    position:        "relative",
  },
  header: {
    display:         "flex",
    alignItems:      "center",
    justifyContent:  "space-between",
    padding:         "max(14px, env(safe-area-inset-top)) 16px 12px",
    backgroundColor: "#FFFFFF",
    flexShrink:      0,
    gap:             8,
  },
  backBtn: {
    width:           36,
    height:          36,
    borderRadius:    "50%",
    border:          "1px solid #EDE8E0",
    background:      "#F8F5F0",
    display:         "flex",
    alignItems:      "center",
    justifyContent:  "center",
    cursor:          "pointer",
    flexShrink:      0,
    transition:      "background 0.15s",
  },
  headerCenter: {
    display:         "flex",
    alignItems:      "center",
    gap:             10,
    flex:            1,
    justifyContent:  "center",
    cursor:          "pointer",
  },
  headerInfo: {
    display:         "flex",
    flexDirection:   "column",
    gap:             1,
  },
  headerName: {
    fontFamily:      "'Cormorant Garamond', Georgia, serif",
    fontSize:        17,
    fontWeight:      700,
    color:           "#0A0A0A",
    letterSpacing:   "-0.02em",
    lineHeight:      1.2,
  },
  headerSub: {
    fontSize:        10,
    color:           "#B8AFA0",
    fontWeight:      400,
    letterSpacing:   "0.04em",
  },
  headerDivider: {
    height:          1,
    flexShrink:      0,
    background:      "linear-gradient(to right, transparent, #EAD08A88 20%, #EAD08A88 80%, transparent)",
  },
  chatOuter: {
    flex:            1,
    position:        "relative",
    minHeight:       0,
    display:         "flex",
    flexDirection:   "column",
  },
  loadMore: {
    display:         "flex",
    alignItems:      "center",
    justifyContent:  "center",
    gap:             6,
    padding:         "8px 0",
    flexShrink:      0,
  },
  loadMoreText: {
    fontSize:        11,
    color:           "#B8AFA0",
    fontFamily:      "'DM Mono', monospace",
    letterSpacing:   "0.06em",
  },
  messagesArea: {
    flex:            1,
    overflowY:       "auto" as const,
    padding:         "12px 16px 8px",
    display:         "flex",
    flexDirection:   "column" as const,
  },
  emptyState: {
    flex:            1,
    display:         "flex",
    flexDirection:   "column",
    alignItems:      "center",
    justifyContent:  "center",
    gap:             12,
    padding:         "60px 32px",
    textAlign:       "center",
  },
  emptyGlyph: {
    fontSize:        32,
    color:           "#C8860A",
    lineHeight:      1,
    marginBottom:    4,
  },
  emptyTitle: {
    fontFamily:      "'Cormorant Garamond', Georgia, serif",
    fontSize:        22,
    fontWeight:      700,
    color:           "#0A0A0A",
    letterSpacing:   "-0.02em",
  },
  emptySub: {
    fontSize:        13,
    color:           "#8A8070",
    maxWidth:        200,
    lineHeight:      1.65,
    fontWeight:      300,
  },
  scrollFab: {
    position:        "absolute",
    bottom:          16,
    right:           16,
    width:           36,
    height:          36,
    borderRadius:    "50%",
    border:          "none",
    cursor:          "pointer",
    display:         "flex",
    alignItems:      "center",
    justifyContent:  "center",
    zIndex:          10,
    background:      "linear-gradient(135deg, #0A0A0A, #2C2C2C)",
    boxShadow:       "0 4px 16px rgba(0,0,0,0.25)",
    transition:      "transform 0.15s",
  },
  warningBar: {
    display:         "flex",
    alignItems:      "center",
    gap:             10,
    padding:         "10px 16px",
    backgroundColor: "#FDF6E3",
    borderTop:       "1px solid #EAD08A55",
    borderBottom:    "1px solid #EAD08A55",
    flexShrink:      0,
  },
  warningText: {
    flex:            1,
    fontSize:        12,
    color:           "#8A6A20",
    fontWeight:      500,
    lineHeight:      1.4,
  },
  warnClose: {
    background:      "none",
    border:          "none",
    cursor:          "pointer",
    padding:         0,
    display:         "flex",
    flexShrink:      0,
  },
  footer: {
    flexShrink:      0,
    backgroundColor: "#FFFFFF",
    borderTop:       "1px solid #EDE8E0",
    padding:         "10px 14px",
    paddingBottom:   "max(10px, env(safe-area-inset-bottom))",
  },
  inputRow: {
    display:         "flex",
    alignItems:      "flex-end",
    gap:             8,
    backgroundColor: "#F8F5F0",
    borderRadius:    24,
    border:          "1px solid #EDE8E0",
    padding:         "8px 6px 8px 16px",
    width:           "100%",
    boxSizing:       "border-box" as const,
  },
  input: {
    flex:            1,
    minWidth:        0,
    border:          "none",
    background:      "transparent",
    resize:          "none",
    fontSize:        16,
    color:           "#0A0A0A",
    fontFamily:      "'DM Sans', sans-serif",
    fontWeight:      400,
    lineHeight:      1.5,
    maxHeight:       120,
    overflowY:       "auto" as const,
    width:           "100%",
    boxSizing:       "border-box" as const,
  } as React.CSSProperties,
  sendBtn: {
    width:           40,
    height:          40,
    borderRadius:    "50%",
    border:          "none",
    display:         "flex",
    alignItems:      "center",
    justifyContent:  "center",
    flexShrink:      0,
    minWidth:        40,
    transition:      "background 0.15s, transform 0.15s, box-shadow 0.15s",
  },
};